import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box, Typography, Chip, IconButton, InputAdornment,
    TextField, MenuItem, Select, Card, CardContent,
    Tooltip, Skeleton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import SearchIcon        from "@mui/icons-material/Search";
import ClearIcon         from "@mui/icons-material/Clear";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import OpenInNewIcon     from "@mui/icons-material/OpenInNew";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import { useNavigate } from "react-router-dom";
import { encryptId } from "../helper/idCodec.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS = {
    Completed: { bg: T.tealLt,    color: T.teal,    icon: "✓",  dotColor: T.teal    },
    Active:    { bg: T.emeraldLt, color: T.emerald, icon: "▶",  dotColor: T.emerald },
    "In-Active":{ bg: T.amberLt,  color: T.amber,   icon: "○",  dotColor: T.amber   },
};

const getStatus = (trip) => {
    if (trip.trip_started && trip.trip_completed) return "Completed";
    if (trip.trip_started && !trip.trip_completed) return "Active";
    return "In-Active";
};

// ── Filter pill ───────────────────────────────────────────────────────────
const FilterPill = ({ label, active, color, bg, onClick }) => (
    <Box onClick={onClick} sx={{
        px: 1.8, py: 0.6, borderRadius: "8px", cursor: "pointer",
        bgcolor: active ? bg : "transparent",
        color:   active ? color : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 12.5,
        border: `1.5px solid ${active ? color + "44" : T.border}`,
        transition: "all 0.14s", whiteSpace: "nowrap",
        "&:hover": { bgcolor: bg, color },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>{label}</Box>
);

// ── Date formatter ────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtRs   = n => n != null ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";

// ── Trip card ─────────────────────────────────────────────────────────────
const TripCard = ({ trip, onView }) => {
    const statusLabel = getStatus(trip);
    const cfg         = STATUS[statusLabel] ?? STATUS["In-Active"];

    return (
        <Card
            sx={{
                borderRadius: "16px", border: `1px solid ${T.border}`,
                boxShadow: "0 1px 4px rgba(15,23,42,0.06)", mb: 2,
                transition: "transform 0.18s, box-shadow 0.18s",
                "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(0,124,132,0.10)" },
                overflow: "hidden", cursor: "pointer",
            }}
            onClick={() => onView(trip.id)}
        >
            {/* Top accent */}
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${cfg.dotColor}, ${cfg.dotColor}55)` }} />

            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>

                    {/* Truck icon box */}
                    <Box sx={{
                        width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                        bgcolor: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <LocalShippingRoundedIcon sx={{ fontSize: 24, color: cfg.color }} />
                    </Box>

                    {/* Main content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Row 1: route + status + dates */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            <Box>
                                {/* Route */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        {trip.trip_started_at || "—"}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Box sx={{ width: 20, height: 1.5, bgcolor: T.muted, borderRadius: 1 }} />
                                        <Box sx={{ width: 0, height: 0, borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderLeft: `6px solid ${T.muted}` }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        {trip.trip_ended_at || "—"}
                                    </Typography>
                                </Box>
                                {/* Trip ID */}
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "7px", px: 1, py: 0.2 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Trip #{trip.id}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Right: dates + status */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                    <CalendarTodayRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                    <Typography sx={{ fontSize: 12, color: T.muted }}>
                                        {fmtDate(trip.actual_start_time)}
                                        {trip.reach_date && (
                                            <> <Box component="span" sx={{ mx: 0.4 }}>→</Box> {fmtDate(trip.reach_date)}</>
                                        )}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={statusLabel}
                                    size="small"
                                    sx={{
                                        height: 22, fontSize: 11, fontWeight: 700, borderRadius: "7px",
                                        bgcolor: cfg.bg, color: cfg.color,
                                        border: `1px solid ${cfg.dotColor}33`,
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Row 2: driver + stats + view */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, pt: 1, borderTop: `1px solid ${T.border}` }}>
                            {/* Driver */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Box sx={{ width: 24, height: 24, borderRadius: "7px", bgcolor: T.indigoLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <PersonRoundedIcon sx={{ fontSize: 14, color: T.indigo }} />
                                </Box>
                                <Typography sx={{ fontSize: 12, color: T.muted }}>
                                    {trip.Drivers?.first_name
                                        ? <><strong style={{ color: T.text }}>{trip.Drivers.first_name}</strong> · ID {trip.Drivers.driver_id}</>
                                        : <span style={{ color: T.muted }}>No driver assigned</span>
                                    }
                                </Typography>
                            </Box>

                            {/* Stats */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                    <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: T.orangeLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Inventory2RoundedIcon sx={{ fontSize: 13, color: T.orange }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: T.muted }}>
                                        <strong style={{ color: T.text }}>{trip.total_packages ?? 0}</strong> pkg{trip.total_packages !== 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                    <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: T.emeraldLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <CurrencyRupeeRoundedIcon sx={{ fontSize: 13, color: T.emerald }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: T.muted }}>
                                        <strong style={{ color: T.text }}>{fmtRs(trip.total_revenue)}</strong>
                                    </Typography>
                                </Box>

                                {/* View details */}
                                <Box
                                    onClick={(e) => { e.stopPropagation(); onView(trip.id); }}
                                    sx={{
                                        display: "flex", alignItems: "center", gap: 0.6,
                                        px: 1.5, py: 0.6, borderRadius: "8px",
                                        bgcolor: T.tealLt, cursor: "pointer",
                                        border: `1px solid ${T.teal}22`,
                                        transition: "background 0.14s",
                                        "&:hover": { bgcolor: T.teal, "& *": { color: "#fff !important" } },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.teal, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        View Details
                                    </Typography>
                                    <OpenInNewIcon sx={{ fontSize: 13, color: T.teal }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Skeleton card ─────────────────────────────────────────────────────────
const TripSkeleton = () => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, mb: 2, overflow: "hidden" }}>
        <Box sx={{ height: 3, bgcolor: T.border }} />
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: "12px", flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Skeleton width={200} height={20} />
                        <Skeleton width={100} height={20} />
                    </Box>
                    <Skeleton width={80} height={16} sx={{ mb: 1.5 }} />
                    <Box sx={{ borderTop: `1px solid ${T.border}`, pt: 1, display: "flex", justifyContent: "space-between" }}>
                        <Skeleton width={120} height={16} />
                        <Skeleton width={160} height={16} />
                    </Box>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Main component ────────────────────────────────────────────────────────
const FILTERS = [
    { label: "All",       value: "",         color: T.teal,   bg: T.tealLt    },
    { label: "Active",    value: "active",   color: T.emerald,bg: T.emeraldLt },
    { label: "Completed", value: "done",     color: T.teal,   bg: T.tealLt    },
    { label: "In-Active", value: "inactive", color: T.amber,  bg: T.amberLt   },
];

const fmtDateLocal = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : date;
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

const TripsList = () => {
    const [trips,        setTrips]        = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [search,       setSearch]       = useState("");
    const [status,       setStatus]       = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentPage,  setCurrentPage]  = useState(0);
    const [perPage,      setPerPage]      = useState(10);
    const navigate = useNavigate();

    const fetchTrips = useCallback(async (searchText = "", statusValue = "", dateValue = null) => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.allTrips, {
                search: searchText,
                status: statusValue,
                timeFilter: "all",
                actual_start_date_from: fmtDateLocal(dateValue),
                actual_start_date_to:   fmtDateLocal(dateValue),
            });
            setTrips(res.result?.trips || []);
        } catch (e) {
            console.error("Failed to fetch trips:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTrips(); }, [fetchTrips]);

    const handleSearch   = (e) => { if (e.key === "Enter") { setCurrentPage(0); fetchTrips(search.trim(), status, selectedDate); } };
    const handleClearSearch = () => { setSearch(""); setCurrentPage(0); fetchTrips("", status, selectedDate); };
    const handleFilter   = (v) => { setStatus(v); setCurrentPage(0); fetchTrips(search.trim(), v, selectedDate); };
    const handleDate     = (d) => { setSelectedDate(d); setCurrentPage(0); fetchTrips(search.trim(), status, d); };
    const handleClearDate= () =>  { setSelectedDate(null); setCurrentPage(0); fetchTrips(search.trim(), status, null); };
    const handleView     = (id) => navigate(`/app/trip-details/${encryptId(id)}`);

    const totalPages  = Math.ceil(trips.length / perPage);
    const paginated   = trips.slice(currentPage * perPage, (currentPage + 1) * perPage);

    const activeCount    = trips.filter(t => t.trip_started && !t.trip_completed).length;
    const completedCount = trips.filter(t => t.trip_completed).length;
    const inactiveCount  = trips.filter(t => !t.trip_started).length;

    return (
        <>
            {/* ── Summary pills ── */}
            {!loading && trips.length > 0 && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
                    {[
                        { label: "Total",     value: trips.length,   color: T.teal,    bg: T.tealLt    },
                        { label: "Active",    value: activeCount,    color: T.emerald, bg: T.emeraldLt },
                        { label: "Completed", value: completedCount, color: T.teal,    bg: T.tealLt    },
                        { label: "In-Active", value: inactiveCount,  color: T.amber,   bg: T.amberLt   },
                    ].map(s => (
                        <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: s.bg, border: `1px solid ${s.color}22`, borderRadius: "10px", px: 1.5, py: 0.7 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.color }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}: {s.value}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── Toolbar ── */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2.5 }}>
                {/* Search */}
                <TextField
                    placeholder="Search by ID, truck, or city…"
                    size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                    sx={{ flex: "1 1 260px", maxWidth: 360 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.muted }} /></InputAdornment>,
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                            </InputAdornment>
                        ) : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                {/* Date picker */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DatePicker
                        label="Filter by date"
                        value={selectedDate}
                        onChange={handleDate}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 }, width: 170 } } }}
                    />
                    {selectedDate && (
                        <Tooltip title="Clear date">
                            <IconButton size="small" onClick={handleClearDate} sx={{ color: T.muted }}>
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* Status filter pills */}
                <Box sx={{ display: "flex", gap: 0.75, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", p: 0.5, flexWrap: "wrap" }}>
                    {FILTERS.map(f => (
                        <FilterPill key={f.value} label={f.label} active={status === f.value} color={f.color} bg={f.bg} onClick={() => handleFilter(f.value)} />
                    ))}
                </Box>

                {/* Result count */}
                {!loading && (
                    <Typography sx={{ fontSize: 13, color: T.muted, ml: "auto" }}>
                        {trips.length} trip{trips.length !== 1 ? "s" : ""}
                    </Typography>
                )}
            </Box>

            {/* ── Trip list ── */}
            {loading
                ? Array.from({ length: perPage }).map((_, i) => <TripSkeleton key={i} />)
                : paginated.length === 0
                ? (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                        <LocalShippingRoundedIcon sx={{ fontSize: 48, color: T.border, mb: 1 }} />
                        <Typography sx={{ color: T.muted, fontSize: 14 }}>No trips found.</Typography>
                    </Box>
                )
                : paginated.map(trip => <TripCard key={trip.id} trip={trip} onView={handleView} />)
            }

            {/* ── Pagination ── */}
            {!loading && trips.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "14px", px: 2.5, py: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: T.muted }}>
                        Showing {Math.min(currentPage * perPage + 1, trips.length)}–{Math.min((currentPage + 1) * perPage, trips.length)} of {trips.length}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(0); }} size="small"
                            sx={{ borderRadius: "10px", fontSize: 13 }}>
                            {[10, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                        </Select>
                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.max(p-1, 0))} disabled={currentPage === 0}
                            sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                            <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                            {currentPage + 1} / {totalPages}
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(p+1, totalPages-1))} disabled={currentPage >= totalPages-1}
                            sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                            <KeyboardArrowRight sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default TripsList;
